"use client";

import { useEffect, useState } from "react";

type UseDerivedVideoPosterInput = {
  videoUrl?: string;
  explicitPosterUrl?: string;
};

type DerivedPosterCacheEntry =
  | {
      status: "pending";
      lastUsed: number;
      promise: Promise<string | undefined>;
    }
  | {
      status: "resolved";
      lastUsed: number;
      posterUrl: string;
    }
  | {
      status: "failed";
      lastUsed: number;
    };

const _DERIVED_POSTER_TIMEOUT_MS = 4_000;
const DERIVED_POSTER_CACHE_LIMIT = 120;
const posterCache = new Map<string, DerivedPosterCacheEntry>();

const now = () => Date.now();

const touchCacheEntry = (videoUrl: string) => {
  const entry = posterCache.get(videoUrl);
  if (!entry) {
    return;
  }

  posterCache.set(videoUrl, {
    ...entry,
    lastUsed: now(),
  });
};

const revokeCacheEntry = (entry: DerivedPosterCacheEntry) => {
  if (entry.status === "resolved") {
    URL.revokeObjectURL(entry.posterUrl);
  }
};

const prunePosterCache = () => {
  if (posterCache.size <= DERIVED_POSTER_CACHE_LIMIT) {
    return;
  }

  const candidates = [...posterCache.entries()]
    .sort(([, a], [, b]) => a.lastUsed - b.lastUsed)
    .slice(0, posterCache.size - DERIVED_POSTER_CACHE_LIMIT);

  for (const [videoUrl, entry] of candidates) {
    if (entry.status === "pending") {
      continue;
    }

    revokeCacheEntry(entry);
    posterCache.delete(videoUrl);
  }
};

const drawPosterFromVideo = async (
  videoUrl: string,
): Promise<string | undefined> => {
  const { Input, UrlSource, ALL_FORMATS, CanvasSink } = await import(
    "mediabunny"
  );

  const input = new Input({
    source: new UrlSource(videoUrl),
    formats: ALL_FORMATS,
  });

  try {
    const videoTrack = await input.getPrimaryVideoTrack();
    if (!videoTrack) {
      return undefined;
    }

    const duration = await input.computeDuration();
    const targetTimeSec = Number.isFinite(duration)
      ? Math.min(1, Math.max(0, duration * 0.25))
      : 0;

    const sink = new CanvasSink(videoTrack);
    const wrappedCanvas = await sink.getCanvas(targetTimeSec);
    if (!wrappedCanvas) {
      return undefined;
    }

    const canvas = wrappedCanvas.canvas;
    const blob = await new Promise<Blob | null>((resolve) => {
      if (canvas instanceof HTMLCanvasElement) {
        canvas.toBlob((result) => resolve(result), "image/jpeg", 0.86);
      } else {
        canvas
          .convertToBlob({ type: "image/jpeg", quality: 0.86 })
          .then(resolve)
          .catch(() => resolve(null));
      }
    });

    if (!blob) {
      return undefined;
    }

    return URL.createObjectURL(blob);
  } catch (error) {
    console.error(
      "[useDerivedVideoPoster] Failed to draw poster using mediabunny:",
      error,
    );
    return undefined;
  } finally {
    input.dispose();
  }
};

const getDerivedPosterFromCache = (videoUrl: string): string | undefined => {
  const entry = posterCache.get(videoUrl);
  if (!entry) {
    return undefined;
  }

  touchCacheEntry(videoUrl);
  if (entry.status === "resolved") {
    return entry.posterUrl;
  }

  return undefined;
};

const getOrCreateDerivedPoster = async (
  videoUrl: string,
): Promise<string | undefined> => {
  const existing = posterCache.get(videoUrl);
  if (existing) {
    touchCacheEntry(videoUrl);
    if (existing.status === "resolved") {
      return existing.posterUrl;
    }
    if (existing.status === "failed") {
      return undefined;
    }
    return existing.promise;
  }

  const pendingPromise = drawPosterFromVideo(videoUrl).then((posterUrl) => {
    if (posterUrl) {
      posterCache.set(videoUrl, {
        status: "resolved",
        lastUsed: now(),
        posterUrl,
      });
      prunePosterCache();
      return posterUrl;
    }

    posterCache.set(videoUrl, {
      status: "failed",
      lastUsed: now(),
    });
    prunePosterCache();
    return undefined;
  });

  posterCache.set(videoUrl, {
    status: "pending",
    lastUsed: now(),
    promise: pendingPromise,
  });

  return pendingPromise;
};

export const useDerivedVideoPoster = ({
  videoUrl,
  explicitPosterUrl,
}: UseDerivedVideoPosterInput): string | undefined => {
  const [derivedPosterUrl, setDerivedPosterUrl] = useState<string | undefined>(
    () => {
      if (explicitPosterUrl) {
        return explicitPosterUrl;
      }

      if (!videoUrl) {
        return undefined;
      }

      return getDerivedPosterFromCache(videoUrl);
    },
  );

  useEffect(() => {
    if (explicitPosterUrl) {
      setDerivedPosterUrl(explicitPosterUrl);
      return;
    }

    if (!videoUrl) {
      setDerivedPosterUrl(undefined);
      return;
    }

    const cachedPoster = getDerivedPosterFromCache(videoUrl);
    if (cachedPoster) {
      setDerivedPosterUrl(cachedPoster);
      return;
    }

    let isActive = true;
    void getOrCreateDerivedPoster(videoUrl).then((posterUrl) => {
      if (!isActive) {
        return;
      }

      setDerivedPosterUrl(posterUrl);
    });

    return () => {
      isActive = false;
    };
  }, [videoUrl, explicitPosterUrl]);

  return explicitPosterUrl ?? derivedPosterUrl;
};
